import React, {
  createRef,
  forwardRef,
  useContext,
  useEffect,
  useRef,
  type RefObject,
} from 'react';
import { css, useTheme } from '@emotion/react';
import {
  RestModalAction,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import { RefsContext } from '@bbkFunnelUtils/refs.context';

type Props = {
  id: string;
  otherActions: RestModalForwards[];
  onClick: (action: RestModalForwards, args?: unknown) => void;
  includeOr?: boolean;
  includeOrText?: string;
  hide?: () => void;
  version2Template?: boolean;
};

const OtherActionBtn = forwardRef<
  HTMLButtonElement,
  Pick<Props, 'version2Template' | 'onClick' | 'hide'> & {
    action: RestModalForwards;
  }
>(({ action, version2Template, onClick, hide }, ref) => {
  const theme = useTheme() as EmotionTheme;
  const labelColor =
    theme.buttonBackgroundColor || theme.primaryColor || 'inherit';

  const btnStyles = version2Template
    ? {
        background: 'transparent',
        color: labelColor,
        border: `2px solid ${labelColor}`,
        marginTop: '1rem',
        width: '100%',
      }
    : {
        background: 'transparent',
        color: labelColor,
        fontSize: 16,
        boxShadow: 'none',
      };

  const btnClasses = 'd-inline-flex align-items-center justify-content-center';

  const iconStyles = version2Template
    ? {
        marginRight: 4,
      }
    : {
        color: labelColor,
        marginRight: 4,
        marginBottom: -2,
      };

  const renderButtonIcon = (
    otherAction: RestModalForwards,
    icon?: React.ComponentProps<typeof ActionButtonIcons>['icon']
  ): React.ReactNode => {
    if (otherAction.icon !== 'none') {
      return (
        <ActionButtonIcons
          icon={otherAction.icon || icon}
          styles={iconStyles}
        />
      );
    }
    return undefined;
  };

  switch (action.action) {
    case RestModalAction.link:
    case RestModalAction.accept_offer_with_confirmation:
      return (
        <FunnelButton
          ref={ref}
          classes={btnClasses}
          specificStyles={btnStyles}
          onClick={() => onClick(action)}
        >
          {renderButtonIcon(action, 'bbk-esi-phone')}
          {action.text || 'Schedule a call'}
        </FunnelButton>
      );

    case RestModalAction.modal_email:
    case RestModalAction.modal_email_structured:
    case RestModalAction.send_email:
      return (
        <FunnelButton
          ref={ref}
          classes={btnClasses}
          specificStyles={btnStyles}
          onClick={() => onClick(action, 'other_actions')}
        >
          {renderButtonIcon(action, 'bbk-esi-message')}
          {action.text || 'Email'}
        </FunnelButton>
      );

    case RestModalAction.dismiss_modal:
      return (
        <FunnelButton
          ref={ref}
          classes={btnClasses}
          specificStyles={btnStyles}
          onClick={hide}
        >
          {action.icon && renderButtonIcon(action)}
          {action.text || 'Continue to cancel'}
        </FunnelButton>
      );

    case RestModalAction.nevermind:
    case RestModalAction.cancel_with_link:
      return (
        <FunnelButton
          ref={ref}
          classes={btnClasses}
          specificStyles={btnStyles}
          onClick={() => onClick(action)}
        >
          {action.icon && renderButtonIcon(action)}
          {action.text}
        </FunnelButton>
      );
    default:
      return null;
  }
});

export const OtherActions: React.FC<Props> = (props) => {
  const { id, otherActions, includeOr, includeOrText, version2Template } =
    props;
  const theme = useTheme() as EmotionTheme;
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const btnRefs = useRef<RefObject<HTMLButtonElement>[]>([]);
  useEffect(() => {
    btnRefs.current.forEach((btn, idx) =>
      refsCtx?.addRef(`${refsPrefix}-btn${idx}`, btn)
    );
  }, [btnRefs?.current.length]);

  useEffect(() => {
    btnRefs.current = otherActions.map(
      (x, i) => btnRefs.current[i] ?? createRef<HTMLButtonElement>()
    );
  }, [otherActions.length]);

  const equalButtonSize = theme.complianceEqualButtonSize;
  return (
    <div
      className={cs('other-options', {
        'one-option': otherActions.length === 1,
        'tw-text-center': otherActions.length === 1 && !equalButtonSize,
        'tw-flex tw-justify-center': equalButtonSize,
      })}
    >
      {includeOr && includeOrText && (
        <span
          className="lead"
          css={css`
            opacity: 0.5;
            font-size: 0.8em;
            color: #000000;
            text-align: center;
            margin-right: 5px;
            ${
              version2Template &&
              css`
                display: block;
                margin-top: 1rem;
              `
            }
          `}
        >
          {includeOrText}
        </span>
      )}
      {otherActions.map((action, idx) => (
        <OtherActionBtn
          action={action}
          {...props}
          key={idx}
          ref={btnRefs.current[idx]}
        />
      ))}
    </div>
  );
};
